import { useMutation } from "@tanstack/react-query";
import { captureException } from "@sentry/react";
import { isAxiosError } from "axios";
import { apiClient, signApiRequest } from "./";
import type { CustomerCredentials } from "../ui/customerDataStore";

interface DeleteResult {
  deletedFiles: number;
  deletedRows: number;
}

const deleteInsurelyData = async (
  credentials: CustomerCredentials,
  leadId?: string,
): Promise<DeleteResult> => {
  const leadParam = leadId ? `&leadId=${leadId}` : "";
  const url = `${import.meta.env.VITE_API_URL}/mis/insurely-delete?fid=${credentials.identityId}${leadParam}`;
  const signedRequest = await signApiRequest("DELETE", url, credentials);
  const response = await apiClient(signedRequest);
  return response.data;
};

export const useDeleteInsurelyData = (credentials: CustomerCredentials) =>
  useMutation<DeleteResult, Error, { leadId?: string }>({
    mutationFn: ({ leadId }) => deleteInsurelyData(credentials, leadId),
    // A silent retry would hide a partial delete the user has to see.
    retry: false,
    onError: (error) => {
      const status = isAxiosError(error) ? error.response?.status : undefined;
      captureException(
        new Error(
          `deleteInsurelyData failed${status ? ` (status ${status})` : ""}`,
        ),
        {
          tags: {
            operation: "deleteInsurelyData",
            errorType: error.name || "UnknownError",
          },
          extra: { fid: credentials.identityId, status },
          level: "error",
        },
      );
    },
  });
